/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 그리드의 목록형 필터 클래스를 정의한다.
 *
 * @file /src/models/table/filters/List.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 8. 30.
 */
import * as parent from './Filter';
import { Container } from '@components/form/field/container/Container';
import { ListStores, TreeStores } from '@models/store/Store';
import { Record } from '@models/store/Record';
import { Dom } from '@utils/Dom';
import { List as Panel } from '@components/list/List';
import { Grouper } from '@models/table/Group';
import { Search } from '@components/form/field/search/Search';
import { Language } from '@utils/Language';
import { Toolbar } from '@components/toolbar/Toolbar';

export interface Properties<S extends ListStores | TreeStores = ListStores | TreeStores> extends parent.Properties {
    /**
     * 필터타입
     */
    type: 'list';

    /**
     * 목록 너비
     */
    width?: number;

    /**
     * 목록 최소 높이
     */
    minHeight?: number;

    /**
     * 목록 최대 높이
     */
    maxHeight?: number;

    /**
     * 데이터스토어
     */
    store: S;

    /**
     * 그룹설정
     */
    grouper?: Grouper<Panel<S>>;

    /**
     * 표시될 필드명
     */
    displayField?: string;

    /**
     * 필터값 필드명
     */
    valueField?: string;

    /**
     * 선택가능여부
     */
    selectable?: boolean | ((record: Record) => boolean);

    /**
     * 다중선택여부
     */
    multiple?: boolean;

    /**
     * 검색여부
     */
    search?: boolean;

    /**
     * 컬럼 렌더러
     */
    renderer?: (display: string, record: Record, $dom: Dom) => string | Promise<string>;
}

export class List<S extends ListStores | TreeStores = ListStores | TreeStores> extends parent.Filter {
    /**
     * 필터타입
     */
    type: string = 'list';

    /**
     * 목록 너비
     */
    width: number;

    /**
     * 목록 최소 높이
     */
    minHeight: number;

    /**
     * 목록 최대 높이
     */
    maxHeight: number;

    /**
     * 데이터스토어
     */
    store: S;

    /**
     * 그룹설정
     */
    grouper: Grouper<Panel<S>>;

    /**
     * 표시될 필드명
     */
    displayField: string;

    /**
     * 필터값 필드명
     */
    valueField: string;

    /**
     * 선택가능여부
     */
    selectable: boolean | ((record: Record) => boolean);

    /**
     * 다중선택여부
     */
    multiple: boolean;

    /**
     * 검색여부
     */
    search: boolean;

    /**
     * 컬럼 렌더러
     */
    renderer: ((display: string, record: Record, $dom: Dom) => string | Promise<string>) | null;

    /**
     * 검색필드
     */
    keyword: Search = null;

    /**
     * 목록
     */
    list: Panel<S> = null;

    /**
     * 선택 필드
     */
    field: Container = null;

    /**
     * 목록형 필터를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties<S>) {
        super(properties);

        this.width = properties?.width ?? 200;
        this.minHeight = properties?.minHeight ?? 100;
        this.maxHeight = properties?.maxHeight ?? 400;
        this.store = properties?.store ?? null;
        this.grouper = properties?.grouper ?? null;
        this.displayField = properties?.displayField ?? 'display';
        this.valueField = properties?.valueField ?? 'value';
        this.selectable = properties?.selectable ?? true;
        this.multiple = properties?.multiple === true;
        this.search = properties?.search === true;
        this.renderer = properties?.renderer ?? null;
        this.store.setPrimaryKeys([this.valueField], false);
    }

    /**
     * 검색필드를 가져온다.
     *
     * @returns 검색필드
     */
    getKeyword(): Search {
        if (this.keyword === null) {
            this.keyword = new Search({
                flex: 1,
                liveSearch: true,
                handler: async (keyword, me) => {
                    me.getParents(Panel<S>)
                        .getStore()
                        .setFilters(
                            {
                                [this.displayField]: { value: keyword, operator: 'likecode' },
                                [this.valueField]: { value: keyword, operator: 'likecode' },
                            },
                            'OR'
                        );
                },
            });
        }

        return this.keyword;
    }

    /**
     * 목록 가져온다.
     *
     * @returns 목록
     */
    getList(): Panel<S> {
        if (this.list === null) {
            this.list = new Panel<S>({
                store: this.store,
                selectable: this.selectable,
                autoLoad: false,
                layout: 'auto',
                minHeight: this.minHeight,
                maxHeight: this.maxHeight,
                multiple: this.multiple === true,
                autoExpand: true,
                border: true,
                topbar: ((search: boolean) => {
                    if (search == true) {
                        return new Toolbar({
                            tools: [this.getKeyword()],
                            scrollable: false,
                        });
                    }
                    return null;
                })(this.search),
                grouper: this.properties.grouper,
                dataIndex: (_, record, $column) => {
                    const display = record.get(this.displayField) ?? '';
                    if (this.renderer !== null) {
                        return this.renderer(display, record, $column);
                    }
                    return display;
                },
                listeners: {
                    selectionChange: async (selections) => {
                        if (selections.length > 0) {
                            this.getButton().setText(
                                Language.get('models.table.filter.confirm_with_count', {
                                    count: selections.length.toString(),
                                })
                            );
                        } else {
                            this.getButton().setText(Language.get('models.table.filter.confirm'));
                        }
                    },
                },
            });
        }

        return this.list;
    }

    /**
     * 필터의 최대높이를 설정한다.
     *
     * @param maxHeight - 최대높이
     */
    setMaxHeight(maxHeight: number): void {
        this.getList().setMaxHeight(maxHeight - 60);
    }

    /**
     * 필터필드를 가져온다.
     *
     * @returns 필드
     */
    getField(): Container {
        if (this.field === null) {
            this.field = new Container({
                items: [this.getList()],
                width: this.width,
            });
        }
        return this.field;
    }

    /**
     * 필터객체를 가져온다.
     *
     * @returns 필터객체
     */
    async getFilter(): Promise<{ value: any; operator: string } | null> {
        const value = this.getList()
            .getSelection()
            .getSelections()
            .map((record) => {
                return record.get(this.valueField);
            });

        if (value.length == 0) {
            return null;
        }

        if (this.multiple == true) {
            return { value: value, operator: 'in' };
        } else {
            return { value: value.at(0) ?? null, operator: '=' };
        }
    }

    /**
     * 현재 필터설정을 필터필드에 적용한다.
     *
     * @param filter - 필터객체
     */
    async setFilter(filter: { value: any; operator: string } | null): Promise<void> {
        this.getButton().setDisabled(true);

        if (this.search == true) {
            this.getKeyword().setValue('');
            await this.getList().getStore().setFilters(null);
        }

        if (this.getList().getStore().isLoaded() == false) {
            await this.getList().getStore().load();
        }

        await this.getList().getBody().blurCell();

        let values = filter?.value ?? [];
        if (Array.isArray(values) == false) {
            values = [values];
        }

        const records: Record[] = [];
        for (const value of values) {
            const record = await this.getList()
                .getStore()
                .match({ [this.valueField]: value });
            if (record !== null) {
                records.push(record);
            }
        }

        await this.getList().getSelection().restoreSelections(records, true);
        this.getButton().setDisabled(false);
    }
}
